import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  effect,
  inject,
  signal,
} from "@angular/core";
import {
  GlossInputOptions,
  SyncStatusEnum,
} from "@bitwarden/web-vault/app/models/types/general-types";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import {
  AccountType,
  accountManualType,
} from "@bitwarden/web-vault/app/models/types/account.types";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { CurrencyType } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";

@Component({
  selector: "app-account-form",
  templateUrl: "./account-form.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFormComponent {
  private importStore = inject(ImportStoreService);
  private i18nService = inject(I18nService);
  // Inputs
  accountId = input<string>();
  accountName = input<string>();
  accountNickname = input<string>();
  accountNamePlaceHolder = input<string>();
  institutionInputOptions = input<GlossInputOptions>();
  selectedInstitution = input<InstitutionView>();
  selectedAccountProduct = input<InstitutionAccountView>();
  saveButtonOptions = input<ActionButton>();
  cancelButtonOptions = input<ActionButton>();
  accountType = input<AccountType>();
  isCurrencyEditMode = input<boolean>(true);
  currency = input<CurrencyType>();
  inWizard = input<boolean>(false);
  isReadOnly = input<boolean>(false);
  isEditMode = input<boolean>(false);

  // Institution selector (create flow) — relocated from the old institution-selection step.
  institutionNames = input<string[]>([]);
  countryNames = input<string[]>([]);
  selectedCountryName = input<string>("");
  // Maps each country name to its ISO 3166-1 alpha-2 code, for rendering flags.
  countryCodes = input<Record<string, string>>({});
  selectedInstitutionName = input<string>("");
  taggedManualInstitutions = input<string[]>([]);
  institutionSearchPlaceholder = input<string>("");
  institutionsIsRefreshing = input<boolean>(false);

  // Show the editable institution selector only when creating; edit mode keeps the fixed display.
  showInstitutionSelect = computed(() => !this.isEditMode() && !this.isFormDisabled());

  // Force the institution field's touched/required state (e.g. when the user clicks the
  // still-disabled account product before picking an institution).
  institutionTouched = signal(false);

  // Account product can't be chosen until an institution is selected.
  isAccountProductDisabled = computed(() => this.isFormDisabled() || !this.selectedInstitution());

  // Before an institution is picked there's no derived default name yet, so show a generic hint.
  // Once selected, the parent sets the real placeholder (e.g. "My <institution> Account").
  accountNameDisplayPlaceholder = computed(
    () => this.accountNamePlaceHolder() || this.i18nService.t("defaultNameBasedOnSelection"),
  );
  isSyncedAccount = computed(() => {
    const id = this.accountId();
    const syncStatuses = this.importStore.syncStatus.syncStatusViews();

    if (!id || !syncStatuses) {
      return false;
    }

    return syncStatuses.some(
      (status) => status.accountId === id && status.key === SyncStatusEnum.synced,
    );
  });

  isFormDisabled = computed(() => this.isSyncedAccount() || this.isReadOnly());

  // Footer (save button) should be visible unless the account is read-only (system account).
  showFooter = computed(() => !this.isReadOnly());

  // Synced accounts may still save a nickname; read-only (system) accounts never render Save at all
  // (showFooter()). Rendered as a copy so a caller that re-enables its button by mutating it after an
  // await (the add-account wizard) can't leave the OnPush button stuck disabled. The copy never
  // disables itself on click, so a caller that must ignore repeat clicks guards its own handler.
  finalSaveButtonOptions = computed(() => {
    const options = this.saveButtonOptions();
    if (!options) {
      return options;
    }

    return { ...options };
  });

  // Outputs
  selectCurrency = output<string>();
  selectAccountProduct = output<InstitutionAccountView>();
  accountNameChanged = output<string>();
  institutionSelected = output<string>();
  institutionCleared = output<void>();
  countrySelected = output<string>();
  addInstitutionClicked = output<void>();
  cancel = output<void>();
  create = output<void>();
  accountNicknameChanged = output<string>();

  accountManualTypeRef = accountManualType;

  isStockOrOther = computed(() => {
    const type = this.accountType();
    return type === accountManualType.stock || type === accountManualType.other;
  });

  institutionPlaceholder = computed(() => {
    if (this.isFormDisabled()) {
      return "";
    }
    return this.isStockOrOther() ? "Other" : "";
  });

  accountProductPlaceholder = computed(() => {
    if (this.isFormDisabled()) {
      return "";
    }
    return this.isStockOrOther() ? "Other" : "";
  });

  onSelectCurrency(currency: string) {
    this.selectCurrency.emit(currency);
  }

  onSelectAccountProduct(institutionAccount: InstitutionAccountView) {
    this.selectAccountProduct.emit(institutionAccount);
  }

  onAccountNameChanged(accountName: string) {
    this.accountNameChanged.emit(accountName);
  }

  onInstitutionSelected(institutionName: string) {
    this.institutionSelected.emit(institutionName);
  }

  onInstitutionCleared() {
    this.institutionCleared.emit();
  }

  onCountrySelected(countryName: string) {
    this.countrySelected.emit(countryName);
  }

  onAddInstitution() {
    this.addInstitutionClicked.emit();
  }

  onCancel() {
    this.cancel.emit();
  }

  onCreate() {
    this.create.emit();
  }

  // Clicking the disabled account product before an institution is picked surfaces the
  // institution field's inline "required" error instead of doing nothing.
  onAccountProductClick() {
    if (!this.isFormDisabled() && !this.selectedInstitution()) {
      this.institutionTouched.set(true);
    }
  }

  onAccountNicknameChanged(nickname: string) {
    const trimmed = nickname?.trim();
    this.accountNicknameChanged.emit(trimmed);
  }
}
